import { useQuery } from "@apollo/client";
import { sortBy } from "lodash";
import { useMemo } from "react";
import Container from "~/components/Container";
import ProductRelated from "~/components/ProductRelated";
import ProductRelatedCart from "~/components/ProductRelatedCart";
import { PATH, RelatedPosition } from "~/constants/enum";
import { GET_RELATED_RULES } from "~/data/product/gql";

type Props = {
  position?: RelatedPosition;
  sku?: string;
  type: "product" | "cart";
  cartId?: string;
  data?: any;
  relatedTopProducts?: any;
  parentSlug?: string;
};

const Related = ({
  type = "product",
  position = RelatedPosition.ProductContentBottom,
  sku,
  cartId,
  data,
  parentSlug,
  relatedTopProducts,
}: Props) => {
  const { data: dataRelatedRules } = useQuery(GET_RELATED_RULES, {
    variables: {
      sku: sku,
      cart_id: cartId,
    },
    skip:
      (!sku && type === "product") || (!cartId && type === "cart") || !!data,
    // context: { fetchOptions: { method: 'GET' } }
  });

  const firstBlockId = (dataRelatedRules?.getRelatedRules ?? data)?.filter(
    (i: any) =>
      [
        RelatedPosition.ProductContentBottom,
        RelatedPosition.ProductContentTop,
      ].includes(i.block_position)
  )?.[0]?.group_id;

  const productByPosition = useMemo(() => {
    const dataTemp = dataRelatedRules?.getRelatedRules ?? data;
    if (!dataTemp) return [];

    let relatedByPosition: any = {};
    dataTemp?.map((item: any) => {
      if (!!relatedByPosition[item.block_position]) {
        relatedByPosition[item.block_position].push(item);
      } else {
        relatedByPosition[item.block_position] = [item];
      }
    });
    if (!relatedByPosition?.[position]) return [];

    return sortBy(relatedByPosition[position], "priority");
  }, [dataRelatedRules, position, data]);

  return (
    <Container className="flex flex-col gap-4">
      {productByPosition.map((item: any, index) => {
        return type === "cart" ? (
          <ProductRelatedCart
            cartId={cartId || ""}
            ruleId={item.group_id}
            limit={item.max_products}
            title={item.block_title || item.name}
          />
        ) : (
          <ProductRelated
            sku={sku}
            ruleId={item.group_id}
            limit={item.max_products}
            title={item.block_title || item.name}
            directLabel={
              firstBlockId === item.group_id && !!parentSlug
                ? "View more"
                : undefined
            }
            directUrl={
              firstBlockId === item.group_id && !!parentSlug
                ? (PATH.ProductSimilarDetail.replace(
                    "[slug]",
                    parentSlug
                  ) as string)
                : undefined
            }
            position={position}
            relatedTopProduct={relatedTopProducts?.find(
              (p: any) => p?.relatedProductsByRule?.id === item.group_id
            )}
          />
        );
      })}
    </Container>
  );
};

export default Related;
